// API Docs endpoint navigator — rendered in the Console's LEFT COLUMN (by ConsoleLayout)
// while on /console/docs, so the API reference has a single, consistent sidebar instead of
// a second stacked panel. Endpoint selection is deep-linked via ?e=<endpointId>, which the
// docs detail page (ApiDocumentation, in console mode) reads back.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Menu, Input, Typography, Tag, Spin, Empty, Button } from "antd";
import { SearchOutlined, LeftOutlined } from "@ant-design/icons";
import axiosInstance from "../../utils/axiosConfig";
import { parsePostmanCollection } from "../../utils/apiCollection";

const { Text } = Typography;

const methodColor = (m) =>
  ({ GET: "green", POST: "blue", PUT: "orange", DELETE: "red" }[m] || "default");

// Keep only endpoints matching the query; keep folders that still have content.
const filterTree = (collections, q) => {
  if (!q.trim()) return collections;
  const needle = q.toLowerCase();
  const walk = (nodes) =>
    (nodes || [])
      .map((c) => {
        const endpoints = (c.endpoints || []).filter(
          (e) =>
            e.name.toLowerCase().includes(needle) ||
            e.path.toLowerCase().includes(needle) ||
            e.method.toLowerCase().includes(needle)
        );
        const folders = walk(c.folders);
        if (endpoints.length || folders.length) return { ...c, endpoints, folders };
        return null;
      })
      .filter(Boolean);
  return walk(collections);
};

const groupKey = (c) => `grp:${c.pathKey}`;

const collectGroupKeys = (collections) => {
  const keys = [];
  const walk = (nodes) =>
    (nodes || []).forEach((c) => {
      keys.push(groupKey(c));
      if (c.folders?.length) walk(c.folders);
    });
  walk(collections);
  return keys;
};

const toMenuItems = (collections) =>
  (collections || []).map((c) => ({
    key: groupKey(c),
    label: c.name,
    children: [
      ...toMenuItems(c.folders),
      ...(c.endpoints || []).map((e) => ({
        key: e.id,
        label: (
          <span style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
            <Tag
              color={methodColor(e.method)}
              style={{
                margin: 0,
                minWidth: 38,
                paddingInline: 4,
                fontSize: 10,
                lineHeight: "18px",
                textAlign: "center",
                flex: "none",
              }}
            >
              {e.method}
            </Tag>
            <span style={{ whiteSpace: "normal", wordBreak: "break-word", flex: 1 }}>
              {e.name}
            </span>
          </span>
        ),
      })),
    ],
  }));

export default function ApiDocsNav({ onBack }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedId = searchParams.get("e") || "";

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const apiKey = localStorage.getItem("apiKey");
      if (!apiKey) {
        setLoading(false);
        return;
      }
      try {
        const res = await axiosInstance.get("/api/apiCollection/docs", { timeout: 10000 });
        if (cancelled) return;
        const parsed = parsePostmanCollection(res.data || {});
        setCollections(parsed); // start collapsed; user expands what they need
      } catch {
        // Detail page surfaces the load error; the nav just stays empty.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tree = useMemo(() => filterTree(collections, query), [collections, query]);
  const items = useMemo(() => toMenuItems(tree), [tree]);

  // While searching, expand everything that survived the filter.
  useEffect(() => {
    if (query.trim()) setOpenKeys(collectGroupKeys(tree));
  }, [query, tree]);

  const onClick = ({ key }) => {
    if (key.startsWith("grp:")) return; // folder header
    navigate(`/console/docs?e=${encodeURIComponent(key)}`);
  };

  return (
    <div className="api-docs-nav" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* antd Menu items are single-line and ellipsis-clipped by default; let long
          endpoint names wrap onto multiple lines instead of being cut off. */}
      <style>{`
        .api-docs-nav .ant-menu-item {
          height: auto;
          min-height: 32px;
          line-height: 1.35;
          padding-top: 6px;
          padding-bottom: 6px;
        }
        .api-docs-nav .ant-menu-item .ant-menu-title-content {
          white-space: normal;
          overflow: visible;
          text-overflow: clip;
        }
      `}</style>
      <div style={{ padding: "12px 12px 8px" }}>
        <Button
          type="text"
          size="small"
          icon={<LeftOutlined />}
          onClick={onBack}
          style={{ paddingLeft: 0, marginBottom: 8 }}
        >
          Menu
        </Button>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          API Docs
        </Text>
        <Input
          allowClear
          size="small"
          placeholder="Search endpoints..."
          prefix={<SearchOutlined />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : items.length ? (
          <Menu
            mode="inline"
            items={items}
            selectedKeys={selectedId ? [selectedId] : []}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            onClick={onClick}
            style={{ borderInlineEnd: "none" }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={query ? "No matching endpoints" : "No endpoints"}
            style={{ marginTop: 32 }}
          />
        )}
      </div>
    </div>
  );
}
